import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { listarAdjuntos, PropositoAdjunto, type Adjunto } from "../api";
import { formatearBytes, formatearFecha } from "../formato";
import SubidorAdjuntoModal from "./SubidorAdjuntoModal";
import VisorAdjunto from "./VisorAdjunto";

type GaleriaProps = {
  mascotaID: string;
  puedeEditar: boolean;
  visitaID?: string;
};

const FILTROS = [
  { valor: null, nombre: "Todos" },
  { valor: PropositoAdjunto.Carnet, nombre: "Carnet y vacunas", icono: "💉" },
  { valor: PropositoAdjunto.Examen, nombre: "Exámenes", icono: "🔬" },
  { valor: PropositoAdjunto.Receta, nombre: "Recetas", icono: "💊" },
  { valor: PropositoAdjunto.FotoPerfil, nombre: "Fotos", icono: "📷" },
  { valor: PropositoAdjunto.Otro, nombre: "Otros", icono: "📁" },
];

const ETIQUETAS_PROPOSITO: Record<number, string> = {
  [PropositoAdjunto.FotoPerfil]: "Foto",
  [PropositoAdjunto.Carnet]: "Carnet",
  [PropositoAdjunto.Receta]: "Receta",
  [PropositoAdjunto.Examen]: "Examen",
  [PropositoAdjunto.Otro]: "Doc",
};

export default function GaleriaAdjuntos({ mascotaID, puedeEditar, visitaID }: GaleriaProps) {
  const [filtroProposito, setFiltroProposito] = useState<PropositoAdjunto | null>(null);
  const [adjuntoSeleccionado, setAdjuntoSeleccionado] = useState<Adjunto | null>(null);
  const [modalSubidaAbierto, setModalSubidaAbierto] = useState(false);

  const queryAdjuntos = useQuery({
    queryKey: ["adjuntos", mascotaID, visitaID, filtroProposito],
    queryFn: () =>
      listarAdjuntos(mascotaID, {
        visitaID,
        proposito: filtroProposito ?? undefined,
      }),
  });

  const adjuntos = queryAdjuntos.data ?? [];

  return (
    <div className="galeria-seccion">
      <div className="galeria-cabecera">
        <div className="galeria-filtros">
          {FILTROS.map((f) => (
            <button
              key={f.valor ?? "todos"}
              type="button"
              className={`filtro-pildora ${filtroProposito === f.valor ? "activa" : ""}`}
              onClick={() => setFiltroProposito(f.valor)}
            >
              {f.icono && `${f.icono} `}
              {f.nombre}
            </button>
          ))}
        </div>

        {puedeEditar && (
          <button
            type="button"
            className="boton-primario boton-chico"
            onClick={() => setModalSubidaAbierto(true)}
          >
            + Subir archivo
          </button>
        )}
      </div>

      {queryAdjuntos.isPending && <p className="muted">Cargando archivos…</p>}
      {queryAdjuntos.error && <p className="err">{queryAdjuntos.error.message}</p>}

      {!queryAdjuntos.isPending && adjuntos.length === 0 && (
        <div className="galeria-vacia">
          <span style={{ fontSize: "2.5rem" }}>📂</span>
          <p className="muted">No hay documentos ni fotos en esta categoría.</p>
          {puedeEditar && (
            <button
              type="button"
              className="boton boton-chico"
              style={{ marginTop: "0.5rem" }}
              onClick={() => setModalSubidaAbierto(true)}
            >
              Subir el primer archivo
            </button>
          )}
        </div>
      )}

      {adjuntos.length > 0 && (
        <div className="galeria-grid">
          {adjuntos.map((adj) => {
            const esImg = adj.contentType.startsWith("image/");
            const esPdf = adj.contentType === "application/pdf";
            const etiqueta = ETIQUETAS_PROPOSITO[adj.proposito] ?? "Doc";

            return (
              <article
                key={adj.adjuntoID}
                className="adjunto-tarjeta"
                onClick={() => setAdjuntoSeleccionado(adj)}
              >
                <div className="adjunto-miniatura">
                  {esImg ? (
                    <img
                      src={adj.thumbnailUrl || adj.url}
                      alt={etiqueta}
                      className="adjunto-img"
                      loading="lazy"
                    />
                  ) : esPdf ? (
                    <div className="adjunto-doc-placeholder pdf">
                      <span className="adjunto-doc-icono">📄</span>
                      <span className="adjunto-doc-tipo">PDF</span>
                    </div>
                  ) : (
                    <div className="adjunto-doc-placeholder">
                      <span className="adjunto-doc-icono">📁</span>
                      <span className="adjunto-doc-tipo">DOC</span>
                    </div>
                  )}
                  <span className="adjunto-badge-proposito">{etiqueta}</span>
                </div>

                <div className="adjunto-meta">
                  <span className="adjunto-fecha">{formatearFecha(adj.creadoUtc)}</span>
                  <span className="adjunto-tamano muted">{formatearBytes(adj.bytes)}</span>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {/* Visor / Lightbox */}
      <VisorAdjunto
        adjunto={adjuntoSeleccionado}
        onCerrar={() => setAdjuntoSeleccionado(null)}
        puedeEditar={puedeEditar}
      />

      {/* Modal de subida */}
      <SubidorAdjuntoModal
        abierto={modalSubidaAbierto}
        onCerrar={() => setModalSubidaAbierto(false)}
        mascotaID={mascotaID}
        visitaID={visitaID}
        propositoInicial={filtroProposito ?? PropositoAdjunto.Carnet}
      />
    </div>
  );
}
